import { useState } from "react";
import { FileText, Loader2, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, tokenHeaders } from "@/lib/api";
import { FeeNotice, useFee, feeText } from "@/components/site/Notices";

const DOCS = [
  ["resume", "Resume / CV", ".jpg,.jpeg,image/jpeg", "JPG / JPEG image only · max 10 MB per page · up to 3 pages"],
  ["photo", "Photograph", ".jpg,.jpeg,.png", "JPG or PNG · max 10 MB"],
  ["certificate", "Educational certificates", ".pdf,.jpg,.jpeg,.png", "PDF, JPG or PNG · max 10 MB · up to 3"],
];

export const MAX_UPLOAD = 10 * 1024 * 1024;
export const TOO_BIG = "File is larger than 10 MB. Please upload a file of 10 MB or less.";

const MULTI = { resume: 3, certificate: 3 };

export const docRequired = (type, job) => type === "resume" || (type === "photo" && job?.require_photo) || (type === "certificate" && job?.require_certificates);

export const StepDocuments = ({ app, job, reload }) => {
  const [busy, setBusy] = useState("");
  const headers = tokenHeaders("application", app.id);
  const upload = async (type, file) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD) return toast.error(TOO_BIG);
    if (type === "resume" && file.type && !/jpe?g/i.test(file.type) && !/\.(jpe?g)$/i.test(file.name || "")) return toast.error("Resume / CV must be a JPG or JPEG image.");
    if (MULTI[type] && app.documents.filter((d) => d.doc_type === type).length >= MULTI[type]) return toast.error(`You can upload up to ${MULTI[type]} files here. Delete one to add another.`);
    setBusy(type);
    const fd = new FormData();
    fd.append("doc_type", type);
    fd.append("file", file);
    try {
      await api.post(`/applications/${app.id}/documents`, fd, { headers });
      toast.success("Uploaded");
      await reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const del = async (docId) => {
    await api.delete(`/applications/${app.id}/documents/${docId}`, { headers });
    reload();
  };
  return (
    <div className="space-y-4">
      {DOCS.map(([type, label, accept, hint]) => {
        const files = app.documents.filter((d) => d.doc_type === type);
        const req = docRequired(type, job);
        if (type === "certificate" && !req && !files.length && !job) return null;
        return (
          <div key={type} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`doc-block-${type}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><p className="font-semibold">{label} {req ? <span className="text-red-600">*</span> : <span className="text-xs font-normal text-slate-500">(optional)</span>}</p><p className="text-xs text-slate-500">{hint}</p></div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-100">
                {busy === type ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {files.length && !MULTI[type] ? "Replace" : files.length ? "Add page" : "Upload"}
                <input type="file" accept={accept} className="hidden" disabled={!!busy} onChange={(e) => { upload(type, e.target.files[0]); e.target.value = ""; }} data-testid={`doc-upload-${type}`} />
              </label>
            </div>
            {files.map((f) => (
              <div key={f.id} className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm" data-testid={`doc-file-${type}`}>
                <span className="flex items-center gap-2 truncate"><FileText className="h-4 w-4 shrink-0" />{f.filename} <span className="text-xs text-slate-500">({Math.round(f.size / 1024)} KB)</span></span>
                <button onClick={() => del(f.id)} className="text-slate-400 hover:text-red-600" data-testid={`doc-delete-${f.id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        );
      })}
      <p className="text-xs text-slate-500">Documents are stored privately and visible only to authorised BMI SP GROUP administrators. Do not upload Aadhaar, PAN or bank details.</p>
    </div>
  );
};

const Section = ({ title, onEdit, rows, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="mb-3 flex items-center justify-between">
      <h3 className="font-display font-bold">{title}</h3>
      {onEdit && <button onClick={onEdit} className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700 hover:underline" data-testid={`${testid}-edit`}><Pencil className="h-3.5 w-3.5" /> Edit</button>}
    </div>
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      {rows.map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="font-medium break-words">{v || "—"}</dd></div>)}
    </dl>
  </div>
);

export const summaryRows = (app) => {
  const p = app.personal, e = app.education || {};
  return {
    job: [["Position", app.job_title], ["Monthly salary", app.salary_text], ["Reference", app.reference]],
    personal: [["Full name", p.full_name], ["Father's name", p.father_name], ["Mother's name", p.mother_name], ["Date of birth", p.dob], ["Gender", p.gender],
      ["Email", `${p.email} (verified)`], ["Mobile", `${p.mobile} (${app.mobile_verified ? "verified" : "unverified"})`], ["Address", p.address], ["District", p.district], ["State", p.state], ["PIN code", p.pincode]],
    education: [["Highest qualification", e.highest_qualification], ["Institution", e.institution], ["Passing year", e.passing_year], ["Additional qualifications", e.additional_qualifications], ["Work experience", e.work_experience], ["Skills", e.skills]],
    docs: app.documents.map((d) => [d.doc_type, d.filename]),
  };
};

export const StepReview = ({ app, goto }) => {
  const s = summaryRows(app);
  return (
    <div className="space-y-4">
      <Section title="Selected position" rows={s.job} testid="review-job" />
      <Section title="Personal details" rows={s.personal} onEdit={() => goto(2)} testid="review-personal" />
      <Section title="Education" rows={s.education} onEdit={() => goto(3)} testid="review-education" />
      <Section title="Documents" rows={s.docs} onEdit={() => goto(4)} testid="review-documents" />
    </div>
  );
};

export const StepFee = ({ consent, setConsent, payments }) => {
  const fee = feeText(useFee());
  return (
  <div className="space-y-5">
    <FeeNotice />
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm">
      <label className="flex items-start gap-3"><Checkbox checked={consent.fee} onCheckedChange={(v) => setConsent({ ...consent, fee: !!v })} data-testid="consent-fee-checkbox" /><span>I understand the {fee} is an application processing/submission fee and that paying it does not guarantee employment, an interview or selection.</span></label>
      <label className="flex items-start gap-3"><Checkbox checked={consent.terms} onCheckedChange={(v) => setConsent({ ...consent, terms: !!v })} data-testid="consent-terms-checkbox" /><span>I have read and accept the <Link to="/policies/application-fee-refund" target="_blank" className="underline">Application Fee & Refund Policy</Link>, <Link to="/policies/recruitment-disclaimer" target="_blank" className="underline">Recruitment Disclaimer</Link> and <Link to="/policies/terms" target="_blank" className="underline">Terms & Conditions</Link>, and I meet the eligibility requirements.</span></label>
    </div>
    {!payments && <p className="rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-800" data-testid="payments-not-configured">Online payment is not configured yet. Your application is saved as a draft — please come back later via My Applications.</p>}
  </div>
  );
};

export const PayButton = ({ busy, disabled, onClick }) => {
  const fee = useFee();
  return (
  <Button className="h-12 rounded-full bg-amber-600 px-8 text-base hover:bg-amber-700" disabled={busy || disabled || !fee} onClick={onClick} data-testid="pay-fee-button">
    {busy && <Loader2 className="h-4 w-4 animate-spin" />} Pay {feeText(fee)} application fee
  </Button>
  );
};
